import React from "react";
import { Header } from "./components/Header";
import { motion } from "motion/react";
import { Outlet, useLocation, useMatches } from "react-router";
import { useUserPrivileges } from "@/hooks/useUserPrivileges";
import "./index.css";
import { useAuth } from "@/Auth";
import { useGetChatFeatureFlag } from "@/hooks/useGetChatFeatureFlag";
import { AI_DASHBOARDS_PATH, AI_SCHEDULES_PATH, CHAT_PATH, DASHBOARDS_ROUTE_ID } from "@/constants";
import { LayoutContext } from "./context";
import { ThoughtSpotStatus, useThoughtSpotStatusStore } from "@/thoughtspot/status";
import { AnalyticsDeprecationNotice } from "@/components/AnalyticsDeprecationNotice";
import { SFullAppProvider } from "@/pages/SEmbed/SFullAppProvider";
import { useSFullAppSession } from "@/pages/SEmbed/SFullAppContext";

export function EmbeddingLayout() {
  return (
    <SFullAppProvider>
      <EmbeddingLayoutContent />
    </SFullAppProvider>
  );
}

function EmbeddingLayoutContent() {
  const location = useLocation();
  const matches = useMatches();

  const { user } = useAuth();

  const privileges = useUserPrivileges();
  const chatFeatureFlag = useGetChatFeatureFlag();
  const fullApp = useSFullAppSession();
  const thoughtSpotStatus = useThoughtSpotStatusStore((state) => state.status);

  const isSearchEnabled = privileges.search_data;
  const isThoughtSpotAvailable = thoughtSpotStatus === ThoughtSpotStatus.Authenticated;
  const isSearchPath = location.pathname === "/answer";

  const isChatEnabled = chatFeatureFlag.data?.enabled ?? false;
  const isChatPath =
    location.pathname === CHAT_PATH || location.pathname.startsWith(`${CHAT_PATH}/`);
  const isAIDashboardsEnabled = isChatEnabled;
  const isAIDashboardsPath =
    location.pathname === AI_DASHBOARDS_PATH ||
    location.pathname.startsWith(`${AI_DASHBOARDS_PATH}/`);
  const isAISchedulesPath =
    location.pathname === AI_SCHEDULES_PATH ||
    location.pathname.startsWith(`${AI_SCHEDULES_PATH}/`);

  const isSpotterEnabled = privileges.spotter && !isChatEnabled && isThoughtSpotAvailable;
  const isSpotterPath = location.pathname === "/spotter";
  const isDashboardsPath = matches.some((match) => match.id === DASHBOARDS_ROUTE_ID);

  const showHeader =
    !(fullApp.enabled && isChatPath) &&
    Boolean(user?.is_root || isSearchEnabled || isSpotterEnabled || isChatEnabled);

  return (
    <LayoutContext.Provider value={{ headerIsShown: showHeader }}>
      <div className="flex flex-col h-full bg-lightGray">
        <motion.div
          className="lite-layout-container flex flex-col h-full"
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.75, ease: "easeInOut" }}
        >
          {showHeader && (
            <Header
              isDashboardsPath={isDashboardsPath}
              searchData={{
                enabled: isSearchEnabled && isThoughtSpotAvailable,
                isActive: isSearchPath,
              }}
              spotter={{ enabled: isSpotterEnabled, isActive: isSpotterPath }}
              chat={{ enabled: isChatEnabled && !fullApp.enabled, isActive: isChatPath }}
              aiDashboards={{
                enabled: isAIDashboardsEnabled && !fullApp.enabled,
                isActive: isAIDashboardsPath,
              }}
              aiSchedules={{
                enabled: isAIDashboardsEnabled && !fullApp.enabled,
                isActive: isAISchedulesPath,
              }}
              onBusinessChange={fullApp.enabled ? fullApp.switchBusiness : undefined}
            />
          )}
          <AnalyticsDeprecationNotice />
          <div className="flex flex-1 overflow-hidden">
            <Outlet />
          </div>
        </motion.div>
      </div>
    </LayoutContext.Provider>
  );
}
